import type { KnownQuestion } from "./CallScriptManager";

export type UserSpecialty =
  | "DENTAL"
  | "MEDICAL"
  | "MENTAL_HEALTH"
  | "MEDICAL_AND_DENTAL"
  | "UNKNOWN";

/** Mental-health-only known questions (also used by the Mental Health call script template). */
export const MENTAL_HEALTH_ONLY_FIELD_PATHS = [
  "extra_medical_info.is_aba_autism_covered",
  "extra_medical_info.is_cotreatment_allowed",
  "extra_medical_info.authorization_submission_info",
  "extra_medical_info.is_copay_subject_to_out_of_pocket",
  "extra_medical_info.will_copay_be_waived_if_oop_is_met",
  "extra_medical_info.copay_for_home_visit",
  "extra_medical_info.copay_for_center_visit",
  "extra_medical_info.copay_for_telehealth_visit",
  "extra_medical_info.coinsurance_percentage_for_home_visit",
  "extra_medical_info.coinsurance_percentage_for_center_visit",
  "extra_medical_info.coinsurance_percentage_for_telehealth_visit",
  "extra_medical_info.is_deductible_waived_for_home_visit",
  "extra_medical_info.is_deductible_waived_for_center_visit",
  "extra_medical_info.is_deductible_waived_for_telehealth_visit",
  "extra_medical_info.is_aba_coverage_same_office_vs_home",
  "extra_medical_info.do_we_need_to_request_a_special_authorization_for_telehealth_in_addition_to_usual_aba_authorization",
  "extra_medical_info.visit_limit_waived_with_asd_dx",
] as const;

/** Known questions visible only for these specialties (everyone else: hidden). */
const MENTAL_HEALTH_ONLY_PATHS = new Set<string>(MENTAL_HEALTH_ONLY_FIELD_PATHS);

/** Therapy / general medical extra-info paths (not mental-health-only). */
const MEDICAL_ONLY_PATHS = new Set([
  "extra_medical_info.benefit_max_per_year",
  "extra_medical_info.benefit_max_per_office_visit",
  "extra_medical_info.annual_max_number_of_visits",
  "extra_medical_info.specialty_rx_benefits",
  "extra_medical_info.is_telehealth_visit_covered",
  "extra_medical_info.how_is_telehealth_covered",
  "extra_medical_info.copay_structure_per_category_or_per_visit",
  "extra_medical_info.separate_copay_for_telehealth_and_codes_billed_together",
  "extra_medical_info.services_combined_with_other_therapies",
  "extra_medical_info.exclusions_on_policy_for_services",
  "extra_medical_info.is_combined_with_pt_ot",
  "extra_medical_info.visits_counted_as_one_if_same_day",
]);

/** Dental-only maximums fields (ortho / perio). */
const DENTAL_ONLY_MAXIMUMS_PATHS = new Set([
  "maximums.orthodontics_coverage_limit",
  "maximums.orthodontics_lifetime_maximum",
  "maximums.orthodontics_maximum_benefits_per_case",
  "maximums.orthodontics_lifetime_maximum_used",
  "maximums.orthodontics_deductible_used",
  "maximums.orthodontic_deductible",
  "maximums.periodontic_maximum_used",
  "maximums.periodontic_maximum",
  "maximums.orthodontics_coverage_limit_remaining",
]);

const DENTAL_ONLY_PROCEDURE_CATEGORY_PREFIXES = [
  "categories.ENDODONTICS.",
  "categories.ORTHODONTICS.",
  "categories.PERIODONTICS.",
  "categories.ORAL_SURGERY.",
  "categories.DENTURES.",
  "categories.CROWNS.",
];

/** Fallback match when results_field_path differs in datastore. */
const DENTAL_ONLY_QUESTION_TEXTS = new Set([
  "what is the orthodontics coverage limit?",
  "what is the orthodontics lifetime maximum?",
  "what is the orthodontics maximum benefits per case?",
  "how much of the orthodontics lifetime maximum has been used?",
  "how much of the orthodontic deductible has been used?",
  "what is the orthodontic deductible?",
  "how much of the periodontic maximum has been used?",
  "what is the periodontic maximum?",
  "what is the orthodontics coverage limit remaining?",
  "is endodontics category covered?",
  "is orthodontics category covered?",
  "is periodontics category covered?",
  "is oral surgery category covered?",
  "is dentures category covered?",
  "is crowns category covered?",
]);

function isDentalOnlyProcedureCategoryPath(path: string): boolean {
  return DENTAL_ONLY_PROCEDURE_CATEGORY_PREFIXES.some((prefix) =>
    path.startsWith(prefix),
  );
}

function isDentalOnlyMaximumsOrBenefitsQuestion(question: KnownQuestion): boolean {
  const path = question.results_field_path;
  if (path && DENTAL_ONLY_MAXIMUMS_PATHS.has(path)) {
    return true;
  }
  if (path && isDentalOnlyProcedureCategoryPath(path)) {
    return true;
  }
  const normalizedText = question.question_text.trim().toLowerCase();
  return DENTAL_ONLY_QUESTION_TEXTS.has(normalizedText);
}

function getAllowedSpecialties(question: KnownQuestion): UserSpecialty[] | null {
  if (question.category === "ExtraDentalInfo") {
    return ["DENTAL", "MEDICAL_AND_DENTAL"];
  }

  const path = question.results_field_path;
  if (path?.startsWith("extra_dental_info.")) {
    return ["DENTAL", "MEDICAL_AND_DENTAL"];
  }

  if (isDentalOnlyMaximumsOrBenefitsQuestion(question)) {
    return ["DENTAL", "MEDICAL_AND_DENTAL"];
  }

  if (path && MENTAL_HEALTH_ONLY_PATHS.has(path)) {
    return ["MENTAL_HEALTH"];
  }

  if (path && MEDICAL_ONLY_PATHS.has(path)) {
    return ["MEDICAL", "MEDICAL_AND_DENTAL"];
  }

  if (question.category === "ExtraMedicalInfo") {
    return ["MEDICAL", "MENTAL_HEALTH", "MEDICAL_AND_DENTAL"];
  }

  return null;
}

export function isKnownQuestionVisibleForSpecialty(
  question: KnownQuestion,
  specialty: string | null | undefined,
): boolean {
  const allowed = getAllowedSpecialties(question);
  if (!allowed) {
    return true;
  }

  if (!specialty || specialty === "UNKNOWN") {
    return true;
  }

  return allowed.includes(specialty as UserSpecialty);
}

export function filterKnownQuestionsBySpecialty(
  questions: KnownQuestion[],
  specialty: string | null | undefined,
): KnownQuestion[] {
  return questions.filter((question) =>
    isKnownQuestionVisibleForSpecialty(question, specialty),
  );
}

export function buildCategoriesFromQuestions(
  questions: KnownQuestion[],
): Record<string, string[]> {
  const categories: Record<string, string[]> = {};
  for (const question of questions) {
    if (!categories[question.category]) {
      categories[question.category] = [];
    }
    categories[question.category].push(question.id);
  }
  return categories;
}
